'use client';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

const SITE_KEY =
  process.env.NEXT_PUBLIC_YANDEX_SMARTCAPTCHA_SITE_KEY ||
  'ysc1_K3tNL9H4J9rdIwXtXooRgVVUcGAfDh8iYaT3tyYC4c730f34';

const SCRIPT_SRC =
  'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=onYandexSmartCaptchaLoad';

export const SMARTCAPTCHA_LOAD_TIMEOUT_MS = 10_000;
export const SMARTCAPTCHA_TOKEN_TIMEOUT_MS = 20_000;

type SmartCaptchaApi = {
  render: (
    containerId: string,
    options: {
      sitekey: string;
      invisible: boolean;
      callback: (token: string) => void;
      hideShield?: boolean;
      shieldPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    }
  ) => number;
  execute: (widgetId?: number) => void;
  reset: (widgetId?: number) => void;
};

declare global {
  interface Window {
    smartCaptcha?: SmartCaptchaApi;
    onYandexSmartCaptchaLoad?: () => void;
    __yandexSmartCaptchaReady?: Promise<void>;
    __resolveYandexSmartCaptchaReady?: () => void;
    __rejectYandexSmartCaptchaReady?: (error: Error) => void;
  }
}

function loadSmartCaptchaScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('SmartCaptcha is available only in browser'));
  }

  if (window.smartCaptcha) {
    return Promise.resolve();
  }

  if (!window.__yandexSmartCaptchaReady) {
    window.__yandexSmartCaptchaReady = new Promise<void>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)?.remove();
        window.__yandexSmartCaptchaReady = undefined;
        window.__resolveYandexSmartCaptchaReady = undefined;
        window.__rejectYandexSmartCaptchaReady = undefined;
        reject(new Error('SmartCaptcha не загрузилась вовремя. Попробуйте ещё раз.'));
      }, SMARTCAPTCHA_LOAD_TIMEOUT_MS);

      window.__resolveYandexSmartCaptchaReady = () => {
        window.clearTimeout(timeoutId);
        resolve();
      };
      window.__rejectYandexSmartCaptchaReady = (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      };
      window.onYandexSmartCaptchaLoad = window.__resolveYandexSmartCaptchaReady;
    });
  }

  if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.defer = true;
    script.async = true;
    script.onerror = () => {
      window.__rejectYandexSmartCaptchaReady?.(
        new Error('Не удалось загрузить SmartCaptcha. Проверьте соединение и повторите попытку.')
      );
      window.__yandexSmartCaptchaReady = undefined;
      script.remove();
    };
    document.head.appendChild(script);
  }

  return window.__yandexSmartCaptchaReady;
}

export function useInvisibleSmartCaptcha() {
  const reactId = useId();
  const containerId = `smart-captcha-${reactId.replace(/:/g, '')}`;
  const widgetIdRef = useRef<number | null>(null);
  const tokenResolveRef = useRef<((token: string) => void) | null>(null);
  const tokenRejectRef = useRef<((error: Error) => void) | null>(null);
  const tokenTimeoutRef = useRef<number | null>(null);
  const [isReady, setIsReady] = useState(false);

  const clearTokenTimeout = useCallback(() => {
    if (tokenTimeoutRef.current !== null) {
      window.clearTimeout(tokenTimeoutRef.current);
      tokenTimeoutRef.current = null;
    }
  }, []);

  const renderWidget = useCallback(() => {
    if (!window.smartCaptcha) {
      throw new Error('Капча ещё не загружена');
    }

    if (widgetIdRef.current !== null) {
      return widgetIdRef.current;
    }

    if (!document.getElementById(containerId)) {
      throw new Error('Контейнер капчи не найден');
    }

    widgetIdRef.current = window.smartCaptcha.render(containerId, {
      sitekey: SITE_KEY,
      invisible: true,
      hideShield: true,
      shieldPosition: 'bottom-right',
      callback: (token) => {
        clearTokenTimeout();
        if (token) {
          tokenResolveRef.current?.(token);
        } else {
          tokenRejectRef.current?.(new Error('SmartCaptcha вернула пустой токен. Повторите попытку.'));
        }
        tokenResolveRef.current = null;
        tokenRejectRef.current = null;
      },
    });

    setIsReady(true);
    return widgetIdRef.current;
  }, [clearTokenTimeout, containerId]);

  useEffect(() => {
    loadSmartCaptchaScript()
      .then(() => {
        if (document.getElementById(containerId)) {
          renderWidget();
        }
      })
      .catch((error) => {
        tokenRejectRef.current?.(error);
      });
  }, [containerId, renderWidget]);

  const executeCaptcha = useCallback(() => {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Капча недоступна в серверном окружении'));
    }

    return new Promise<string>((resolve, reject) => {
      const run = () => {
        if (!window.smartCaptcha) {
          reject(new Error('Капча ещё не загружена'));
          return;
        }

        const widgetId = renderWidget();
        clearTokenTimeout();
        tokenRejectRef.current?.(new Error('Предыдущая проверка SmartCaptcha отменена новой попыткой.'));
        tokenResolveRef.current = resolve;
        tokenRejectRef.current = reject;
        tokenTimeoutRef.current = window.setTimeout(() => {
          tokenResolveRef.current = null;
          tokenRejectRef.current = null;
          tokenTimeoutRef.current = null;
          reject(new Error('SmartCaptcha не ответила вовремя. Повторите попытку.'));
        }, SMARTCAPTCHA_TOKEN_TIMEOUT_MS);
        window.smartCaptcha.execute(widgetId);
      };

      if (isReady) {
        run();
        return;
      }

      loadSmartCaptchaScript()
        .then(() => {
          window.setTimeout(run, 0);
        })
        .catch(reject);
    });
  }, [clearTokenTimeout, isReady, renderWidget]);

  const resetCaptcha = useCallback(() => {
    clearTokenTimeout();
    const widgetId = widgetIdRef.current;
    if (typeof window !== 'undefined' && window.smartCaptcha && widgetId !== null) {
      window.smartCaptcha.reset(widgetId);
    }
  }, [clearTokenTimeout]);

  useEffect(() => () => {
    clearTokenTimeout();
    tokenRejectRef.current?.(new Error('Проверка SmartCaptcha отменена.'));
    tokenResolveRef.current = null;
    tokenRejectRef.current = null;
  }, [clearTokenTimeout]);

  const captchaElement = <div id={containerId} />;

  return {
    captchaElement,
    executeCaptcha,
    resetCaptcha,
    isCaptchaReady: isReady,
  };
}
